import { Dispatch } from "redux";
import { faEye, faLock } from "@fortawesome/pro-solid-svg-icons";
import {
  faCopy,
  faCrosshairs,
  faCut,
  faDollarSign,
  faPencilAlt,
  faPhotoVideo,
  faPlus,
  faPrint,
  faRedoAlt,
  faTruck,
} from "@fortawesome/free-solid-svg-icons";
import { faContainerStorage } from "@fortawesome/pro-regular-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { MultiButtonOption } from "components/Button/MultiButton/types";
import {
  getCurrentSaleyardAuctionRoute,
  getScanRoute,
  openDeliverSaleLotModal,
  openEditSaleLotModal,
  openModalLink,
  openSplitSaleLotModal,
} from "lib/navigation";
import { ModalTypes, SaleLotModalSection } from "constants/navigation";
import { auctionPenActions } from "constants/sale";
import { patchSaleLot, printPlacard, printSaleLotWeightSheet } from "actions";
import { getSaleLotById, getScannedCountBySaleLotId } from "selectors";
import { PenTypes } from "constants/auctionPens";
import { hasPermission } from "lib/permissions";
import { SaleLotPermission } from "constants/permissions";
import { useHistory } from "react-router-dom";
import { History } from "history";
import { LotButtonOption } from "./buttonOptions";

interface ButtonContext {
  saleLotId: string;
  consignmentId: string;
  buyerId: string;
  history: History;
  dispatch: Dispatch;
  thirdPartyId: string;
  invoiceToBusinessId: string;
  auctionPenId: string;
  hasEditPermission: boolean;
  scanCount: number;
  roundId: number;
  toggleUnsellDialog: (isOpen: boolean) => void;
}

function getViewButton({ saleLotId }: ButtonContext): MultiButtonOption {
  return {
    title: "View Lot",
    icon: faEye,
    dataTour: "view",
    onClick: () => openEditSaleLotModal(saleLotId, SaleLotModalSection.GENERAL),
  };
}

function getEditButton({ saleLotId }: ButtonContext): MultiButtonOption {
  return {
    title: "Edit Lot",
    icon: faPencilAlt,
    dataTour: "edit",
    onClick: () => openEditSaleLotModal(saleLotId, SaleLotModalSection.GENERAL),
  };
}

function getAddBuyerSundryButton({
  buyerId,
}: ButtonContext): MultiButtonOption {
  return {
    title: "Add Buyer Sundry",
    icon: faPlus,
    dataTour: "add-buyer-manual",
    onClick: () =>
      openModalLink(ModalTypes.ManualAdjustmentForm, {
        fromBusinessId: buyerId,
      }),
  };
}

function getAddInvoiceToManualChargeButton({
  invoiceToBusinessId,
}: ButtonContext): MultiButtonOption {
  return {
    title: "Add Invoice To Sundry",
    icon: faPlus,
    dataTour: "add-invoice-to-manual",
    onClick: () =>
      openModalLink(ModalTypes.ManualAdjustmentForm, {
        fromBusinessId: invoiceToBusinessId,
      }),
  };
}
function getAddThirdPartySundryButton({
  thirdPartyId,
}: ButtonContext): MultiButtonOption {
  return {
    title: "Add Third Party Sundry",
    icon: faPlus,
    dataTour: "add-third-party-manual",
    onClick: () =>
      openModalLink(ModalTypes.ManualAdjustmentForm, {
        fromBusinessId: thirdPartyId,
      }),
  };
}

function getScanButton({
  scanCount,
  history,
  saleLotId,
  consignmentId,
}: ButtonContext): MultiButtonOption {
  return {
    title: "Scan",
    icon: faCrosshairs,
    dataTour: "scan",
    onClick: () => {
      if (scanCount > 0) {
        openEditSaleLotModal(
          saleLotId,
          SaleLotModalSection.SCANNING,
          null,
          consignmentId,
        );
      } else {
        history.push(
          `#${getScanRoute(
            null,
            saleLotId,
            true,
            null,
            null,
            null,
            null,
            null,
            PenTypes.SELLING,
          )}`,
        );
      }
    },
  };
}

function getCopyDraftButton({
  consignmentId,
  saleLotId,
}: ButtonContext): MultiButtonOption {
  return {
    title: "Copy Draft",
    icon: faCopy,
    dataTour: "copyDraft",
    onClick: () =>
      openEditSaleLotModal(
        null,
        SaleLotModalSection.GENERAL,
        null,
        consignmentId,
        saleLotId,
      ),
  };
}

function getAddMediaButton({ saleLotId }: ButtonContext): MultiButtonOption {
  return {
    title: "Add Media",
    icon: faPhotoVideo,
    dataTour: "addMedia",
    onClick: () =>
      openEditSaleLotModal(
        saleLotId,
        SaleLotModalSection.MEDIA,
        null,
        null,
        null,
      ),
  };
}

function getSplitLotButton({ saleLotId }: ButtonContext): MultiButtonOption {
  return {
    title: "Split Lot",
    icon: faCut,
    dataTour: "splitLot",
    onClick: () => openSplitSaleLotModal(saleLotId, null, null, null),
  };
}

function getRepenButton({ saleLotId }: ButtonContext): MultiButtonOption {
  return {
    title: "Re-Pen",
    icon: faContainerStorage,
    dataTour: "repen",
    onClick: () =>
      openEditSaleLotModal(
        saleLotId,
        SaleLotModalSection.AUCTION_PEN,
        auctionPenActions.RE_PEN,
      ),
  };
}

function getUnpenButton({
  dispatch,
  saleLotId,
}: ButtonContext): MultiButtonOption {
  return {
    title: "Unpen",
    icon: faRedoAlt,
    dataTour: "unpen",
    onClick: () => {
      const patch = { auction_pen_id: null, id: saleLotId };
      dispatch(
        patchSaleLot(patch, {
          actionText: "unpenned",
          changeReason: "Bulk unpenned",
        }),
      );
    },
  };
}

function getPenButton({
  consignmentId,
  saleLotId,
}: ButtonContext): MultiButtonOption {
  return {
    title: "Pen",
    icon: faContainerStorage,
    dataTour: "pen",
    onClick: () =>
      openEditSaleLotModal(
        saleLotId,
        SaleLotModalSection.AUCTION_PEN,
        auctionPenActions.RE_PEN,
        consignmentId,
      ),
  };
}

function getSellButton({
  history,
  roundId,
  auctionPenId,
}: ButtonContext): MultiButtonOption {
  return {
    title: "Sell",
    icon: faDollarSign,
    dataTour: "sell",
    onClick: () =>
      history.push(getCurrentSaleyardAuctionRoute(roundId, auctionPenId)),
  };
}

function getViewSaleButton({
  history,
  roundId,
  auctionPenId,
}: ButtonContext): MultiButtonOption {
  return {
    title: "View Sale",
    icon: faDollarSign,
    dataTour: "viewSale",
    onClick: () =>
      history.push(getCurrentSaleyardAuctionRoute(roundId, auctionPenId)),
  };
}

function getUnsellButton({
  toggleUnsellDialog,
  hasEditPermission,
}: ButtonContext): MultiButtonOption {
  return {
    title: "Unsell",
    icon: faRedoAlt,
    dataTour: "unsell",
    isDisabled: !hasEditPermission,
    onClick: () => toggleUnsellDialog(true),
  };
}

function getDeliverButton({ saleLotId }: ButtonContext): MultiButtonOption {
  return {
    title: "Deliver",
    icon: faTruck,
    dataTour: "deliver",
    onClick: () => openDeliverSaleLotModal(saleLotId),
  };
}

function getUndeliverButton({
  saleLotId,
  hasEditPermission,
  dispatch,
}: ButtonContext): MultiButtonOption {
  return {
    title: "Undeliver",
    icon: hasEditPermission ? faRedoAlt : faLock,
    dataTour: "undeliver",
    isDisabled: !hasEditPermission,
    onClick: () => {
      dispatch(
        patchSaleLot(
          { quantity_delivered: null, id: saleLotId },
          { actionText: "un-delivered", changeReason: "Bulk un-delivered" },
        ),
      );
    },
  };
}

function getPrintWeightSheetButton({
  dispatch,
  saleLotId,
}: ButtonContext): MultiButtonOption {
  return {
    title: "Print Weight Sheet",
    icon: faPrint,
    dataTour: "printWeightSheet",
    onClick: () => dispatch(printSaleLotWeightSheet(saleLotId, true)),
  };
}

function getPrintPlacardButton({
  dispatch,
  saleLotId,
}: ButtonContext): MultiButtonOption {
  return {
    title: "Placard",
    icon: faPrint,
    dataTour: "printPlacard",
    onClick: () =>
      dispatch(printPlacard(saleLotId, undefined, true, undefined)),
  };
}

const MultiButtonConfigMap = {
  [LotButtonOption.VIEW]: getViewButton,
  [LotButtonOption.EDIT]: getEditButton,
  [LotButtonOption.ADD_BUYER_SUNDRY]: getAddBuyerSundryButton,
  [LotButtonOption.ADD_INVOICE_TO_MANUAL_CHARGE]:
    getAddInvoiceToManualChargeButton,
  [LotButtonOption.ADD_THIRD_PARTY_SUNDRY]: getAddThirdPartySundryButton,
  [LotButtonOption.SCAN]: getScanButton,
  [LotButtonOption.COPY_DRAFT]: getCopyDraftButton,
  [LotButtonOption.ADD_MEDIA]: getAddMediaButton,
  [LotButtonOption.SPLIT_LOT]: getSplitLotButton,
  [LotButtonOption.PEN]: getPenButton,
  [LotButtonOption.REPEN]: getRepenButton,
  [LotButtonOption.UNPEN]: getUnpenButton,
  [LotButtonOption.SELL]: getSellButton,
  [LotButtonOption.VIEW_SALE]: getViewSaleButton,
  [LotButtonOption.UNSELL]: getUnsellButton,
  [LotButtonOption.DELIVER]: getDeliverButton,
  [LotButtonOption.UNDELIVER]: getUndeliverButton,
  [LotButtonOption.PRINT_WEIGHT_SHEET]: getPrintWeightSheetButton,
  [LotButtonOption.PRINT_PLACARD]: getPrintPlacardButton,
};

export function useMapButtonOptionToMultiButtons(
  saleLotId,
  toggleUnsellDialog: (isOpen: boolean) => void,
  buttonOptions: LotButtonOption[],
): MultiButtonOption[] {
  const dispatch = useDispatch();
  const history: History = useHistory();

  const saleLot: SaleLot = useSelector<State, SaleLot>(
    getSaleLotById(saleLotId),
  );

  const scanCount = useSelector<State, number>(
    getScannedCountBySaleLotId(saleLotId),
  );

  const hasEditPermission = hasPermission(saleLot, SaleLotPermission.update);
  const context: ButtonContext = {
    saleLotId,
    consignmentId: saleLot.consignment_id,
    buyerId: saleLot.buyer_id,
    history,
    dispatch,
    thirdPartyId: saleLot.thirdPartyId,
    invoiceToBusinessId: saleLot.invoiceToBusinessId,
    auctionPenId: saleLot.auction_pen_id,
    hasEditPermission,
    scanCount,
    roundId: saleLot.sale_round_id,
    toggleUnsellDialog,
  };

  return buttonOptions.map(option => MultiButtonConfigMap[option](context));
}
